/**
 * The assistant's HTTP surface (spec §9.18): threads, a streamed reply, proposal apply/dismiss. The reply
 * is a `text/event-stream` read from a POST, frame by frame.
 */

export type Mode = "help" | "config" | "overwhelm";

export interface LlmProfile {
  name: "local" | "cloud";
  provider: string;
  model: string;
  cloud: boolean;
}

export interface Proposal {
  id: string;
  kind: "step" | "tool";
  status: "pending" | "applied" | "dismissed" | "failed";
  // step
  action?: "start_timer" | "start_pomodoro" | "split";
  taskId?: string;
  taskTitle?: string;
  reason?: string;
  subtasks?: string[];
  // tool
  tool?: string;
  summary?: string;
  method?: string;
  path?: string;
  args?: Record<string, unknown>;
  error?: string;
}

export interface Message {
  id: number | string;
  role: "user" | "assistant";
  text: string;
  proposals: Proposal[];
  notice?: string | null;
}

export interface Thread {
  id: string;
  title: string;
  mode: Mode;
  llm: "local" | "cloud";
  createdAt: string;
}

export type StreamEvent =
  | { type: "delta"; text: string }
  | { type: "tool"; name: string }
  | { type: "proposal"; proposal: Proposal }
  | { type: "notice"; code: string }
  | { type: "done"; messageId: number; proposals: Proposal[] }
  | { type: "error"; code: string };

const BASE = "/api/internal/assistant";

async function json<T>(res: Response): Promise<T> {
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? "error.generic");
  return data;
}

export async function status(): Promise<{ profiles: LlmProfile[] } | null> {
  const res = await fetch(BASE, { credentials: "same-origin" });
  return res.ok ? ((await res.json()) as { profiles: LlmProfile[] }) : null;
}

/** Ask the server to load the model now, so the first answer does not wait for it. Best effort. */
export function warm(llm: string): void {
  void fetch(`${BASE}/warm`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ llm }),
  }).catch(() => undefined);
}

export async function listThreads(workspaceId: string): Promise<Thread[]> {
  const res = await fetch(`${BASE}/threads?workspace=${encodeURIComponent(workspaceId)}`, {
    credentials: "same-origin",
  });
  return (await json<{ threads: Thread[] }>(res)).threads;
}

export async function createThread(workspaceId: string, mode: Mode, llm: string): Promise<Thread> {
  const res = await fetch(`${BASE}/threads`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ workspace: workspaceId, mode, llm }),
  });
  return (await json<{ thread: Thread }>(res)).thread;
}

export async function loadThread(id: string): Promise<{ thread: Thread; messages: Message[] }> {
  return json(await fetch(`${BASE}/threads/${id}`, { credentials: "same-origin" }));
}

export async function purgeThreads(workspaceId: string): Promise<void> {
  await fetch(`${BASE}/threads?workspace=${encodeURIComponent(workspaceId)}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
}

export async function decide(
  threadId: string,
  proposalId: string,
  action: "apply" | "dismiss",
): Promise<Proposal> {
  const res = await fetch(`${BASE}/threads/${threadId}/proposals/${proposalId}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action }),
  });
  return (await json<{ proposal: Proposal }>(res)).proposal;
}

/** Send a message; `onEvent` receives each frame as it arrives. Resolves when the stream ends. */
export async function send(
  threadId: string,
  text: string,
  language: string,
  onEvent: (e: StreamEvent) => void,
): Promise<void> {
  const res = await fetch(`${BASE}/threads/${threadId}/messages`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, language }),
  });
  if (!res.ok || !res.body) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    onEvent({ type: "error", code: data.error ?? "assistant.failed" });
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let cut: number;
    while ((cut = buffer.indexOf("\n\n")) >= 0) {
      const frame = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      if (frame.startsWith("data: ")) onEvent(JSON.parse(frame.slice(6)) as StreamEvent);
    }
  }
}
